import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  getConfigNotes,
  addConfigNote,
  newConfigNote,
  setConfigNoteLocation,
} from 'utils/project-definition/note-utils';

import DragLayer from './drag-layer';

const NOTE_RADIUS = 12; // width & height: 25px
const INITIAL_CONTENT = '';

// Layer that causes press events (mousedown, touchstart) to create new note objects
export default class NoteEditingLayer extends React.Component {
  constructor(props) {
    super(props);

    this.pressHandler = this.pressHandler.bind(this);
    this.dragHandler = this.dragHandler.bind(this);
    this.releaseHandler = this.releaseHandler.bind(this);
    this.isDragHandler = this.isDragHandler.bind(this);
  }

  addNewNote(x, y) {
    const { config } = this.props;
    if (!config) {
      return null;
    }

    const note = newConfigNote(INITIAL_CONTENT, x, y);

    addConfigNote(config, note);
    if (this.props.onAddedNote) {
      this.props.onAddedNote(note);
    }
    return note;
  }

  getNoteAtCoord(coordX, coordY) {
    const { config } = this.props;
    if (!config) {
      return null;
    }
    const notes = getConfigNotes(config);

    if (!notes) {
      return null;
    }
    const numNotes = notes.length;
    if (!numNotes) {
      return null;
    }
    const tollerance = NOTE_RADIUS;
    const tolleranceSq = tollerance * tollerance;

    for (let i = 0; i < numNotes; i += 1) {
      const note = notes[i];
      const { x, y } = note;
      const dx = coordX - x;
      const dy = coordY - y;
      const distSq = (dx * dx) + (dy * dy);
      if (distSq < tolleranceSq) {
        return note;
      }
    }
    return null;
  }

  // Internal method to transform pointer coords in window space (clientX, clientY) to local space
  // (delegate to external code by 'transformPointerCoords' callback prop)
  transformPointerCoordsFromEventInfo(eventInfo) {
    const { transformPointerCoords } = this.props;
    if (transformPointerCoords) {
      const { x, y } = eventInfo;
      const coord = this.transformedCoord || {};
      this.transformedCoord = coord;
      transformPointerCoords(x, y, coord);
      return coord;
    }
    // return eventInfo because it contains x and y properties
    return eventInfo;
  }

  // Event handlers

  pressHandler(evt) {
    const e = evt;

    if (this.props.onPress) {
      const result = this.props.onPress();
      if (result === false) {
        e.canceled = true;
        return;
      }
    }
    const { x, y } = this.transformPointerCoordsFromEventInfo(e.info);

    let note = this.getNoteAtCoord(x, y);

    if (!note) {
      this.createdNewNote = true;
      note = this.addNewNote(x, y);
    }
    this.draggingNote = note;
  }

  // Check whether a drag has occurred
  isDragHandler(info, prevInfo) {
    const dx = info.x - prevInfo.x;
    const dy = info.y - prevInfo.y;
    const d = (dx * dx) + (dy * dy);
    const toll = 10;
    const tollSq = toll * toll;

    return (d > tollSq);
  }

  dragHandler(evt) {
    const { x, y } = this.transformPointerCoordsFromEventInfo(evt.info);

    const onDragNote = this.props.onDragNote;

    const { draggingNote } = this;

    setConfigNoteLocation(draggingNote, x, y);
    if (onDragNote) {
      onDragNote(draggingNote);
    }
  }

  releaseHandler() {
    const { draggingNote, dragged, createdNewNote } = this;
    if (draggingNote) {
      if (createdNewNote) {
        if (this.props.onCreatedNote) {
          this.props.onCreatedNote(draggingNote);
        }
      } else if (dragged) {
        if (this.props.onDraggedNote) {
          this.props.onDraggedNote(draggingNote);
        }
      } else if (this.props.onClickNote) {
        this.props.onClickNote(draggingNote);
      }
    }
    this.dragged = false;
    this.createdNewNote = false;
    this.draggingNote = null;
  }

  // Render methods

  render() {
    return this.renderDragLayer();
  }

  renderDragLayer() {
    let overlayStyle = null;
    const { style, className, enabled } = this.props;

    if (style) {
      overlayStyle = style;
    }
    const dragLayerClassName = classnames(
      'config-note__editing-layer',
      className,
    );
    if (!enabled) {
      return (
        <div
          style={overlayStyle}
          className={dragLayerClassName}
        />
      );
    }

    return (
      <DragLayer
        style={overlayStyle}
        className={className}
        onStartDrag={this.pressHandler}
        onDrag={this.dragHandler}
        onStopDrag={this.releaseHandler}
        isDrag={this.isDragHandler}
      />
    );
  }
}

NoteEditingLayer.defaultProps = {
  enabled: true,
};

NoteEditingLayer.propTypes = {
  style: PropTypes.object,
  className: PropTypes.string,
  enabled: PropTypes.bool,

  config: PropTypes.object,
  onAddedNote: PropTypes.func, // Triggers on mousedown / touchstart
  onClickNote: PropTypes.func, // Triggers on mouseup / touchstart if no drag occurred
  onDragNote: PropTypes.func,
  onDraggedNote: PropTypes.func,
  onPress: PropTypes.func,
  onCreatedNote: PropTypes.func, // Triggers on mouseup / touchend

  transformPointerCoords: PropTypes.func,
};



// WEBPACK FOOTER //
// ./js/components/configuration/note-editing-layer.jsx